import { useCallback, useEffect, useState } from 'react';

import { Container, useTheme, useMediaQuery } from '@mui/material';

import { HeadingTag } from '@/types';
import {
	GoogleMapTemplate as GoogleMapTemplateType,
	Maybe,
	Asset,
	BffFacility,
	BffFacilityLocation,
	BffBrandType,
	GoogleMapTemplateNoResultsFoundMessage,
	Button,
	Header,
	MultiCardV2 as TMultiCardV2,
	ImageAsset as TImageAsset,
	GoogleMapTemplateSingleComingSoonMessage,
	Tabs,
} from '@/types/generated';
import { useFacilityContext } from '@/context';
import { useAppContext } from '@/context/AppContextProvider.ctx';
import { getBffNearByFacilitiesByLocation } from '@/services/getBffNearByFacilitiesByLocation';
import { getBffFacilities, getBffFacilitiesByAddress } from '@/services';
import { USA_CENTER_COORDINATES, USA_DEFAULT_DESKTOP_ZOOM, USA_DEFAULT_MOBILE_ZOOM } from '@/constants';
import { textToJumpLink } from '@/utils/textToJumpLink/textToJumpLink';

import { getContainerStyles } from './GoogleMap.styles';
import { RequestStatus, getUsersPosition } from './GoogleMap.helpers';
import StandardGoogleMap from './StandardGoogleMap';
import ModernGoogleMap from './ModernGoogleMap';
import CumulativeGoogleMap from './CumulativeGoogleMap';

interface NextActionProps {
	continentalUSZoom?: boolean;
	reqStatus: RequestStatus;
	dist?: number;
	attempt: number;
	threshold?: number;
}

const initialReason = ({ dist, continentalUSZoom, reqStatus, threshold }: NextActionProps): boolean =>
	!continentalUSZoom &&
	reqStatus === RequestStatus.Success &&
	typeof dist === 'number' &&
	typeof threshold === 'number' &&
	dist > threshold;

interface IActionReason {
	reason: boolean | string;
	result: string;
}

const actionReasons = (props: NextActionProps): IActionReason[] => {
	return [
		{
			reason: initialReason(props),
			result: `focused call succeeded, but closest facility outside distance threshold at ${props.dist ?? 0} mi`,
		},
		{
			reason: !!(!props.continentalUSZoom && props.reqStatus === RequestStatus.Failure),
			result: 'focused call failed',
		},
		{
			reason: !!(props.continentalUSZoom && props.reqStatus === RequestStatus.Failure),
			result: 'extents was contintental US, and call failed',
		},
		{
			reason: !!props.continentalUSZoom,
			result: 'extents was contintental US, and call succeeded',
		},
		{
			reason: 'default',
			result: `focused call succeeded, and closest facility within distance threshold at ${
				typeof props.dist === 'number' ? props.dist : 'unknown'
			} mi`,
		},
	];
};

export const nextActionReason = (props: NextActionProps): string =>
	`attempt ${props.attempt}: ${
		actionReasons(props).find((r) => r.reason === true || r.reason === 'default')?.result ?? ''
	}`;

const nextActions = ({ continentalUSZoom, reqStatus, dist, threshold }: NextActionProps) => {
	const actions = [
		{
			condition: !!(
				!continentalUSZoom &&
				((reqStatus === RequestStatus.Success &&
					typeof dist === 'number' &&
					typeof threshold === 'number' &&
					dist > threshold) ||
					reqStatus === RequestStatus.Failure)
			),
			result: 'fireSecond',
		},
		{
			condition: !!(continentalUSZoom && reqStatus === RequestStatus.Failure),
			result: 'showError',
		},
	];
	return actions.find((a) => a.condition === true)?.result ?? 'doNothing';
};

export const genNextAction = (props: NextActionProps) => ({
	action: nextActions(props),
	reason: nextActionReason(props),
});

export interface IFacilitiesData {
	items: BffFacility[];
	reqStatus?: RequestStatus;
}

export interface IGoogleMap extends GoogleMapTemplateType {
	/**
	 * Optional field for the title of the Google Map
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	title?: Maybe<string>;
	/**
	 * Title heading that will set the H-tag to appear in the HTML without effecting the style of the element. normal will render a paragraph tag.
	 * @type {HeadingTag}
	 * @memberof IGoogleMap
	 * @default "h1"
	 */
	titleHtag?: Maybe<HeadingTag>;
	/**
	 * Optional field for the desktop zoom level
	 * Default: 10
	 *
	 * @type {number}
	 * @memberof IGoogleMap
	 */
	desktopZoom?: Maybe<number>;
	/**
	 * Optional field for the mobile zoom level
	 * Default: 9
	 *
	 * @type {number}
	 * @memberof IGoogleMap
	 */
	mobileZoom?: Maybe<number>;

	/**
	 * Optional field for default image
	 *
	 * @type {Asset}
	 * @memberof IGoogleMap
	 */
	defaultImage?: Maybe<Asset>;

	/**
	 * Optional field for selected image
	 *
	 * @type {Asset}
	 * @memberof IGoogleMap
	 */
	selectedImage?: Maybe<Asset>;
	/**
	 * Optional field for Google Map URL for business query
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	googleMapsBusinessQuery?: Maybe<string>;
	/**
	 * Optional field for customized holiday hours message text
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	holidayHoursMessageText?: Maybe<string>;
	/**
	 * Optional field Rich Text for no results found message
	 *
	 * @type {ContentTypeRichTextContent}
	 * @memberof IGoogleMap
	 */
	noResultsFoundMessage?: Maybe<GoogleMapTemplateNoResultsFoundMessage>;
	/**
	 * Optional field Rich Text for single coming soon location message
	 *
	 * @type {ContentTypeRichTextContent}
	 * @memberof IGoogleMap
	 */
	singleComingSoonMessage?: Maybe<GoogleMapTemplateSingleComingSoonMessage>;
	/**
	 * Optional field for no results found icon. Only for modern design
	 *
	 * @type {Maybe<ImageAsset>}
	 * @memberof IPatientReviews
	 */
	noResultsFoundImage?: Maybe<TImageAsset>;

	/**
	 * Optional field for full width
	 *
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	fullWidth?: boolean;

	/**
	 * Optional field for scrolling the List Tab Horizontally
	 *
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	listTabIsHorizontalScrolling?: boolean;

	/**
	 * Optional field for badge display
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	facilityBadgeLabel?: string;

	/**
	 * Optional field for facility details button
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	facilityButtonLabel?: string;
	/**
	 * Optional field for hiding the map and using only the location cards. Standard design only.
	 * @type {boolean}
	 * @memberof IGoogleMap
	 */
	isLocationCardsInHeader?: boolean;
	/**
	 * The Schedule button on the location cards.
	 *
	 * @type {Maybe<Button>}
	 * @memberof IGoogleMap
	 */
	scheduleButton?: Maybe<Button>;
	/**
	 * The Office Details button on the location cards.
	 *
	 * @type {Maybe<Button>}
	 * @memberof IGoogleMap
	 */
	officeDetailsButton?: Maybe<Button>;
	locationBarFindOfficeUrl?: Header['locationBarFindOfficeUrl'];
	/**
	 * The switch to use the modern design for the location search.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	useModernDesign?: Maybe<boolean>;

	/**
	 * The switch to use the cumulative design with state-grouped locations.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IGoogleMap
	 */
	useCumulativeDesign?: Maybe<boolean>;

	/**
	 * Optional field for no results content
	 *
	 * @type {MultiCardV2}
	 * @memberof IGoogleMap
	 */
	noResultsContent?: Maybe<TMultiCardV2>;
	/**
	 * A list of Services provided as Tabs > Accordion
	 *
	 * @type {Maybe<Tabs>}
	 * @memberof IGoogleMap
	 */
	availableServicesTabs?: Maybe<Tabs>;
	/**
	 * Set as office btn text if feature is true
	 *
	 * @type {string}
	 * @memberof IGoogleMap
	 */
	setAsOfficeButtonText?: string;
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IGoogleMap
	 */
	anchorId?: Maybe<string>;
}

const continentalUSZoomAndCenter = {
	mapCenter: USA_CENTER_COORDINATES,
	desktopZoom: USA_DEFAULT_DESKTOP_ZOOM,
	mobileZoom: USA_DEFAULT_MOBILE_ZOOM,
};

/**
 * Google Map component that supports three different display modes:
 *
 * 1. Standard Map (default):
 *    - Traditional layout with map and location cards
 *    - Supports location search and nearby facility display
 *
 * 2. Modern Map (useModernDesign=true):
 *    - Enhanced UI with modern styling
 *    - Includes available services tabs
 *    - Improved search experience
 *    - Supports custom no-results content
 *
 * 3. Cumulative Map (useCumulativeDesign=true):
 *    - Groups locations by state
 *    - Shows all facilities at once
 *    - Optimized for viewing large numbers of locations
 *    - Takes precedence over useModernDesign if both are true
 */

export default function GoogleMap(props: Readonly<IGoogleMap>) {
	const {
		desktopZoom = 10,
		mobileZoom = 9,
		title,
		titleHtag = 'h1',
		defaultImage,
		selectedImage,
		googleMapsBusinessQuery,
		holidayHoursMessageText,
		noResultsFoundMessage,
		singleComingSoonMessage,
		noResultsFoundImage,
		fullWidth = true,
		listTabIsHorizontalScrolling = false,
		facilityBadgeLabel,
		facilityButtonLabel,
		isLocationCardsInHeader = false,
		scheduleButton,
		officeDetailsButton,
		locationBarFindOfficeUrl,
		useModernDesign = false,
		useCumulativeDesign = false,
		noResultsContent,
		availableServicesTabs,
		setAsOfficeButtonText,
		anchorId,
	} = props;
	const [searchValue, setSearchValue] = useState('');

	const [facilitiesData, setFacilitiesData] = useState<IFacilitiesData>({
		items: [],
		reqStatus: RequestStatus.NotAsked,
	});

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { facilityCode, facilityBrand, officeInfo, nearbyFacilities, isWaitingForNearbyFacilities } =
		useFacilityContext(true);
	const { environment, config, appName, appVersion } = useAppContext();
	const { enforceContinentalUsZoom = false, continentalUsMapFallbackThreshold = 50 } = config?.featureFlags || {};
	const searchParams = globalThis?.location?.search ? new URLSearchParams(globalThis?.location?.search) : null;
	const searchQueryValueFromUrl = searchParams?.get('query') ?? '';

	/**
	 * Determines the fetching strategy for facilities:
	 * - 'all': Fetches all facilities at once, used when:
	 *    1. Continental US zoom is enforced (for wider geographic coverage)
	 *    2. Cumulative design is enabled (needs all locations for state grouping)
	 * - 'one': Fetches facilities based on current location or search query
	 *    Used for standard and modern designs to show nearby/relevant locations
	 */
	const [fetchType, setFetchType] = useState(enforceContinentalUsZoom || useCumulativeDesign ? 'all' : 'one');
	const [nextAction, setNextAction] = useState({ action: 'doNothing', reason: 'Component initialization' });

	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const googleMapId =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_ID : config.services.nonprod.GOOGLE_MAP_ID;
	const googleMapApiKey =
		environment === 'prod' ? config.services.prod.GOOGLE_MAP_API_KEY : config.services.nonprod.GOOGLE_MAP_API_KEY;

	const facilityBasePath = config.features.otherApps.facilityDetailsBasePath;
	const shouldShowSearch = !config.featureFlags?.hideSearchWithLocationCards;
	const shouldUseGoogleAutocompleteSearch = config.featureFlags?.useGoogleMapAutocompleteSearch;
	const fetchAllLocationsOnEmptyResults = config.featureFlags?.fetchAllLocationsOnEmptyResults;

	const currentFacilityLatitude = officeInfo?.location?.latitude as number;
	const currentFacilityLongitude = officeInfo?.location?.longitude as number;

	const updateState = useCallback(
		(newState: Partial<IFacilitiesData>) => {
			setFacilitiesData({
				...facilitiesData,
				...newState,
			});
		},
		[setFacilitiesData, facilitiesData]
	);

	const updateDataFromLocationSearch = (facilitiesByAddress: BffFacility[]) => {
		updateState({
			items: facilitiesByAddress,
		});
	};

	const revertToCurrentFacility = async () => {
		const position = await getUsersPosition();
		//TODO: Need to implement fallback position coordinates from CDN. (EX: User denies Location permissions)
		void fetchData(position.coords.latitude, position.coords.longitude, '');
	};

	const handleRevertToCurrentFacility = () => {
		void revertToCurrentFacility();
	};

	const fetchAllFacilities = useCallback(
		async (skip: number, take: number) => {
			updateState({
				reqStatus: RequestStatus.Loading,
			});
			const facilities = await getBffFacilities(
				facilityBrand.toString() as BffBrandType,
				skip,
				take,
				url,
				appName,
				appVersion
			);
			const facilityCollection = facilities?.facilityCollection?.items;
			updateState({
				items: (facilityCollection as BffFacility[]) || [],
				reqStatus: RequestStatus.Success,
			});
			setSearchValue(searchQueryValueFromUrl);
			setNextAction(
				genNextAction({
					continentalUSZoom: true,
					reqStatus: RequestStatus.Success,
					attempt: nextAction.action === 'fireSecond' ? 2 : 1,
				})
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appName, appVersion, facilityBrand, searchQueryValueFromUrl, url, updateState]
	);

	const fetchData = useCallback(
		async (latitude: number, longitude: number, searchQueryValueFromUrl?: string) => {
			updateState({
				reqStatus: RequestStatus.Loading,
			});

			try {
				if ((!latitude || !longitude) && !searchQueryValueFromUrl) {
					throw new Error('no location info or search query');
				}
				// do not use search string if contains near
				const searchString = searchQueryValueFromUrl?.includes('near') ? '' : searchQueryValueFromUrl ?? '';
				let facilities;

				if (searchString) {
					const facilitiesListByAddress = await getBffFacilitiesByAddress(
						facilityBrand.toString() as BffBrandType,
						searchString,
						url,
						fetchAllLocationsOnEmptyResults,
						appName,
						appVersion
					);
					facilities = facilitiesListByAddress?.facilitiesByAddress;
				} else {
					const facilitiesListByLocation = await getBffNearByFacilitiesByLocation(
						facilityBrand.toString() as BffBrandType,
						latitude,
						longitude,
						url,
						appName,
						appVersion
					);
					facilities = facilitiesListByLocation?.facilityByLocation;
				}

				const closestLoc = facilities?.[0] ?? { location: {} };
				const { location } = closestLoc;
				const { distance } = location as BffFacilityLocation;

				updateState({
					items: (facilities as BffFacility[]) || [],
					reqStatus: RequestStatus.Success,
				});
				setSearchValue(searchQueryValueFromUrl ?? '');

				setNextAction(
					genNextAction({
						continentalUSZoom: enforceContinentalUsZoom,
						reqStatus: RequestStatus.Success,
						dist: typeof distance === 'number' && distance > -1 ? distance : 25000,
						threshold: continentalUsMapFallbackThreshold,
						attempt: 1,
					})
				);
			} catch (e) {
				updateState({
					reqStatus: RequestStatus.Failure,
				});
				setNextAction(
					genNextAction({
						continentalUSZoom: true,
						reqStatus: RequestStatus.Failure,
						attempt: 1,
					})
				);
				setFetchType('all');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appName, appVersion, facilityBrand, updateState, url]
	);

	useEffect(() => {
		if (nearbyFacilities !== undefined) {
			if (fetchType === 'all') {
				void fetchAllFacilities(0, 1000);
			} else if (nearbyFacilities?.length && !searchQueryValueFromUrl) {
				updateState({
					items: nearbyFacilities,
					reqStatus: RequestStatus.Success,
				});
			} else {
				void fetchData(currentFacilityLatitude, currentFacilityLongitude, searchQueryValueFromUrl);
			}
		} else if (!isWaitingForNearbyFacilities) {
			updateState({
				reqStatus: RequestStatus.Success,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		nearbyFacilities,
		fetchType,
		searchQueryValueFromUrl,
		currentFacilityLatitude,
		currentFacilityLongitude,
		isWaitingForNearbyFacilities,
	]);

	const googleMapsSectionProps = {
		nearByFacilities: facilitiesData.items,
		facilityBasePath,
		isSmallScreen,
		currentFacilityLatitude,
		currentFacilityLongitude,
		facilityBrand,
		themeId: config.themeId,
		fullWidth,
		mobileZoom,
		desktopZoom,
		mapCenter: {
			latitude: facilitiesData.items[0]?.location?.latitude ?? currentFacilityLatitude,
			longitude: facilitiesData.items[0]?.location?.longitude ?? currentFacilityLongitude,
		},
		googleMapId,
		googleMapApiKey,
		...(fetchType === 'all' || !currentFacilityLatitude || !currentFacilityLongitude
			? continentalUSZoomAndCenter
			: {}),
	};

	const featureList = config?.featureFlags?.featureList || [];
	const hasOnPageSchedulingEnabled = featureList.includes('onPageSchedulingPilotEnabled');
	const pilotLocations = new Set(config?.featureFlags?.onPageSchedulingLocations || []);
	const showSetOfficeButtonInGoogleMapsCards = config?.featureFlags?.showSetOfficeButtonInGoogleMapsCards;

	const nearByLocationsCardsProps = {
		defaultImage,
		selectedImage,
		googleMapsBusinessQuery,
		facilityBasePath,
		isSmallScreen,
		nearByFacilities: facilitiesData.items,
		currentFacilityId: facilityCode?.toString() ?? '',
		holidayHoursMessageText,
		noResultsFoundMessage,
		pilotLocations: hasOnPageSchedulingEnabled ? pilotLocations : new Set<string>(),
		facilityBadgeLabel,
		facilityButtonLabel,
		scheduleButton,
		officeDetailsButton,
		isLocationCardsInHeader,
		setAsOfficeButtonText,
		showSetOfficeButtonInGoogleMapsCards,
	};

	if (useCumulativeDesign) {
		return (
			<CumulativeGoogleMap
				title={title}
				titleHtag={titleHtag}
				facilitiesData={facilitiesData}
				googleMapsSectionProps={googleMapsSectionProps}
				isSmallScreen={isSmallScreen}
				facilityBasePath={facilityBasePath}
				anchorId={anchorId}
			/>
		);
	}

	return (
		<Container
			{...(anchorId && { id: textToJumpLink(anchorId) })}
			data-test-id="google_maps_template_container"
			sx={getContainerStyles(theme, fullWidth)}
			maxWidth={false}
		>
			{useModernDesign ? (
				<ModernGoogleMap
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					facilitiesData={facilitiesData}
					googleMapsSectionProps={googleMapsSectionProps}
					nearByLocationsCardsProps={{
						...nearByLocationsCardsProps,
						noResultsFoundImage,
						singleComingSoonMessage,
						availableServicesTabs,
					}}
					fullWidth={fullWidth}
					isSmallScreen={isSmallScreen}
					shouldShowSearch={shouldShowSearch}
					updateDataFromLocationSearch={updateDataFromLocationSearch}
					handleRevertToCurrentFacility={handleRevertToCurrentFacility}
					noResultsContent={noResultsContent}
				/>
			) : (
				<StandardGoogleMap
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					searchTitle={title}
					searchTitleHtag={titleHtag}
					facilitiesData={facilitiesData}
					googleMapsSectionProps={googleMapsSectionProps}
					nearByLocationsCardsProps={nearByLocationsCardsProps}
					fullWidth={fullWidth}
					isSmallScreen={isSmallScreen}
					isLocationCardsInHeader={isLocationCardsInHeader}
					shouldShowSearch={shouldShowSearch}
					listTabIsHorizontalScrolling={listTabIsHorizontalScrolling}
					updateDataFromLocationSearch={updateDataFromLocationSearch}
					handleRevertToCurrentFacility={handleRevertToCurrentFacility}
					locationBarFindOfficeUrl={locationBarFindOfficeUrl}
					useGoogleSearch={shouldUseGoogleAutocompleteSearch}
				/>
			)}
		</Container>
	);
}
